import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Consultant, type Organization, type UserRow } from '../api';
import { useMe } from '../auth';
import { ActiveBadge, Dialog, ErrorAlert, Field, fieldError, Loading } from '../components/ui';
import { dateTime } from '../format';
import { IntegrationsSettings } from './Integracoes';
import { AlertPreferencesForm } from './PreferenciasAlertas';

type Tab = 'empresa' | 'usuarios' | 'integracoes' | 'alertas';

export function ConfiguracoesPage() {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'empresa';
  const setTab = (t: Tab) => setParams({ tab: t });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Configurações</h1>
        </div>
        <div className="actions">
          <Link className="btn" to="/minha-senha">
            Alterar minha senha
          </Link>
        </div>
      </div>
      <div className="segmented" role="tablist" style={{ marginBottom: '1rem' }}>
        <button role="tab" aria-pressed={tab === 'empresa'} aria-selected={tab === 'empresa'} onClick={() => setTab('empresa')}>
          Empresa
        </button>
        {me.isAdmin && (
          <button role="tab" aria-pressed={tab === 'usuarios'} aria-selected={tab === 'usuarios'} onClick={() => setTab('usuarios')}>
            Usuários
          </button>
        )}
        <button role="tab" aria-pressed={tab === 'integracoes'} aria-selected={tab === 'integracoes'} onClick={() => setTab('integracoes')}>
          Integrações
        </button>
        <button role="tab" aria-pressed={tab === 'alertas'} aria-selected={tab === 'alertas'} onClick={() => setTab('alertas')}>
          Meus alertas
        </button>
      </div>
      {tab === 'empresa' && <OrganizationForm />}
      {tab === 'usuarios' && me.isAdmin && <UsersAdmin />}
      {tab === 'integracoes' && <IntegrationsSettings />}
      {tab === 'alertas' && <AlertPreferencesForm />}
    </>
  );
}

function OrganizationForm() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['organization'], queryFn: () => api.get<Organization>('/organization') });
  const [form, setForm] = useState<Omit<Organization, 'id' | 'version'> | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (q.data) {
      const { id: _id, version: _v, ...rest } = q.data;
      setForm({ ...rest });
    }
  }, [q.data]);
  const m = useMutation({
    mutationFn: () => api.put('/organization', { ...form, version: q.data!.version }),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
  if (q.isLoading || !form) return <Loading />;
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setSaved(false);
    setForm((f) => (f ? { ...f, [k]: v } : f));
  };
  const text = (k: keyof typeof form) => (form[k] as string | null) ?? '';
  const err = m.error;

  function submit(e: FormEvent) {
    e.preventDefault();
    m.mutate();
  }

  return (
    <form className="card form" onSubmit={submit} noValidate style={{ maxWidth: 860 }}>
      <h2>Identidade da TECHNOVE</h2>
      {form.isDemo && (
        <div className="alert alert-warning">
          <strong>Dados de demonstração</strong>
          Confirme os dados oficiais da empresa e desmarque “dados de demonstração” abaixo.
        </div>
      )}
      <ErrorAlert error={err} />
      {saved && <div className="alert alert-success">Dados da empresa salvos.</div>}
      <div className="form-row">
        <Field label="Nome" required>
          {(p) => <input {...p} value={text('name')} onChange={(e) => set('name', e.target.value)} />}
        </Field>
        <Field label="Razão social">{(p) => <input {...p} value={text('legalName')} onChange={(e) => set('legalName', e.target.value)} />}</Field>
      </div>
      <div className="form-row">
        <Field label="CNPJ" error={fieldError(err, 'cnpj')}>
          {(p) => <input {...p} inputMode="numeric" value={text('cnpj')} onChange={(e) => set('cnpj', e.target.value)} />}
        </Field>
        <Field label="Telefone" error={fieldError(err, 'phone')}>
          {(p) => <input {...p} type="tel" value={text('phone')} onChange={(e) => set('phone', e.target.value)} />}
        </Field>
      </div>
      <div className="form-row">
        <Field label="E-mail" error={fieldError(err, 'email')}>
          {(p) => <input {...p} type="email" value={text('email')} onChange={(e) => set('email', e.target.value)} />}
        </Field>
        <Field label="Site">{(p) => <input {...p} value={text('website')} onChange={(e) => set('website', e.target.value)} />}</Field>
      </div>
      <Field label="Endereço">{(p) => <textarea {...p} value={text('address')} onChange={(e) => set('address', e.target.value)} />}</Field>
      <div className="form-row">
        <Field label="Cor principal" error={fieldError(err, 'primaryColor')}>
          {(p) => <input {...p} type="color" value={text('primaryColor') || '#0b5c8c'} onChange={(e) => set('primaryColor', e.target.value)} />}
        </Field>
        <Field label="Cor secundária" error={fieldError(err, 'secondaryColor')}>
          {(p) => <input {...p} type="color" value={text('secondaryColor') || '#f2a900'} onChange={(e) => set('secondaryColor', e.target.value)} />}
        </Field>
      </div>
      <Field label="Assinatura padrão" hint="Usada nos e-mails e relatórios (etapa P4).">
        {(p) => <textarea {...p} value={text('signature')} onChange={(e) => set('signature', e.target.value)} />}
      </Field>
      <LogoUploader />
      <label className="check">
        <input type="checkbox" checked={form.isDemo} onChange={(e) => set('isDemo', e.target.checked)} /> Dados de demonstração (ainda não confirmados)
      </label>
      <div className="actions">
        <button className="btn btn-primary" disabled={m.isPending}>
          Salvar
        </button>
      </div>
    </form>
  );
}

function UsersAdmin() {
  const me = useMe();
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<{ data: UserRow[]; emailConfigured: boolean; appBaseUrl: string }>('/users') });
  const consultants = useQuery({ queryKey: ['consultants'], queryFn: () => api.get<{ data: Consultant[] }>('/consultants') });
  const [creating, setCreating] = useState(false);
  const [editingEmail, setEditingEmail] = useState<UserRow | null>(null);
  const [inviting, setInviting] = useState<UserRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ title: string; value: string; notice: string } | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['users'] });
    void qc.invalidateQueries({ queryKey: ['consultants'] });
  };
  const patch = useMutation({ mutationFn: ({ id, body }: { id: string; body: object }) => api.patch(`/users/${id}`, body), onSuccess: refresh });
  const reset = useMutation({
    mutationFn: (u: UserRow) => api.post<{ url: string; expiresAt: string }>(`/users/${u.id}/reset-link`),
    onSuccess: (r) => setSecret({ title: 'Link de redefinição de senha', value: r.url, notice: `Válido até ${dateTime(r.expiresAt)}, uso único. Envie por canal privado.` }),
  });

  if (users.isLoading) return <Loading />;
  const withoutAccount = consultants.data?.data.filter((c) => !c.userId && c.active) ?? [];
  const emailConfigured = users.data?.emailConfigured ?? false;
  const appBaseUrl = users.data?.appBaseUrl ?? '';

  return (
    <section className="card">
      <div className="card-head">
        <h2>Contas de acesso</h2>
        <button className="btn btn-sm btn-primary" disabled={withoutAccount.length === 0} onClick={() => setCreating(true)}>
          Criar conta
        </button>
      </div>
      <p className="small muted">
        Cada consultor tem conta individual e entra com o e-mail de login.{' '}
        {emailConfigured
          ? 'Use "Enviar convite" para que a pessoa crie a própria senha pelo e-mail.'
          : 'O envio de e-mail não está configurado (aba Integrações): senhas temporárias e links são exibidos aqui uma única vez para entrega por canal privado.'}
      </p>
      <ErrorAlert error={users.error ?? patch.error ?? reset.error} />
      {notice && <div className="alert alert-success">{notice}</div>}
      <div className="list">
        {users.data?.data.map((u) => (
          <div key={u.id} className="list-item">
            <div className="title">
              {u.consultantName} <ActiveBadge active={u.active} /> {u.isAdmin && <span className="badge badge-brand">Administrador</span>}{' '}
              {u.mustChangePassword && <span className="badge">Senha temporária</span>}
            </div>
            <div className="meta">
              <span>{u.email}</span>
              <span>Último acesso: {dateTime(u.lastLoginAt)}</span>
              {u.lastInviteAt && <span>Convite enviado: {dateTime(u.lastInviteAt)}</span>}
            </div>
            <div className="actions" style={{ marginTop: '0.5rem' }}>
              <button className="btn btn-sm" onClick={() => setEditingEmail(u)}>
                Alterar e-mail
              </button>
              {u.id !== me.id && (
                <>
                {u.active && (
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={!emailConfigured}
                    title={emailConfigured ? undefined : 'Configure o e-mail em Configurações › Integrações'}
                    onClick={() => setInviting(u)}
                  >
                    {u.lastInviteAt ? 'Reenviar convite' : 'Enviar convite'}
                  </button>
                )}
                <button className="btn btn-sm" onClick={() => reset.mutate(u)}>
                  Gerar link de redefinição
                </button>
                <button className="btn btn-sm" onClick={() => patch.mutate({ id: u.id, body: { isAdmin: !u.isAdmin } })}>
                  {u.isAdmin ? 'Remover administrador' : 'Tornar administrador'}
                </button>
                <button
                  className={`btn btn-sm ${u.active ? 'btn-danger' : ''}`}
                  onClick={() =>
                    (!u.active || window.confirm(`Inativar a conta de ${u.consultantName}? A produção histórica é preservada.`)) &&
                    patch.mutate({ id: u.id, body: { active: !u.active } })
                  }
                >
                  {u.active ? 'Inativar conta' : 'Reativar conta'}
                </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      {editingEmail && (
        <EditEmailDialog
          user={editingEmail}
          isSelf={editingEmail.id === me.id}
          onClose={() => setEditingEmail(null)}
          onSaved={() => {
            setEditingEmail(null);
            refresh();
          }}
        />
      )}
      {inviting && (
        <InviteDialog
          user={inviting}
          appBaseUrl={appBaseUrl}
          onClose={() => setInviting(null)}
          onSent={(email) => {
            setInviting(null);
            refresh();
            setNotice(`Convite enviado para ${email}. O link vale por 72 horas e pode ser usado uma vez.`);
          }}
        />
      )}
      {creating && (
        <CreateUserDialog
          consultants={withoutAccount}
          emailConfigured={emailConfigured}
          appBaseUrl={appBaseUrl}
          onClose={() => setCreating(false)}
          onCreated={(r) => {
            setCreating(false);
            refresh();
            if (r.invitedEmail) setNotice(`Conta criada e convite enviado para ${r.invitedEmail}. O link vale por 72 horas.`);
            else setSecret({ title: 'Senha temporária', value: r.temporaryPassword, notice: r.inviteError ? `O convite não foi enviado (${r.inviteError}). ${r.notice}` : r.notice });
          }}
        />
      )}
      {secret && (
        <Dialog title={secret.title} onClose={() => setSecret(null)}>
          <div className="alert alert-warning">{secret.notice}</div>
          <input readOnly value={secret.value} onFocus={(e) => e.target.select()} aria-label={secret.title} />
          <div className="actions" style={{ marginTop: '1rem' }}>
            <button className="btn" onClick={() => void navigator.clipboard?.writeText(secret.value)}>
              Copiar
            </button>
            <button className="btn btn-primary" onClick={() => setSecret(null)}>
              Fechar
            </button>
          </div>
        </Dialog>
      )}
    </section>
  );
}

const isLocalUrl = (url: string) => /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|\/|$)/i.test(url);

// Aviso: enquanto o app roda só neste computador, o link do convite não abre no aparelho de quem recebe.
function LocalLinkWarning({ appBaseUrl }: { appBaseUrl: string }) {
  if (!isLocalUrl(appBaseUrl)) return null;
  return (
    <div className="alert alert-warning">
      <strong>O link do convite aponta para {appBaseUrl}</strong>
      Esse endereço só funciona neste computador. Para quem recebe abrir em outro aparelho, o aplicativo precisa estar publicado (etapa E10) ou acessível na
      rede (APP_BASE_URL).
    </div>
  );
}

function InviteDialog({ user, appBaseUrl, onClose, onSent }: { user: UserRow; appBaseUrl: string; onClose: () => void; onSent: (email: string) => void }) {
  const m = useMutation({ mutationFn: () => api.post<{ email: string }>(`/users/${user.id}/invite`), onSuccess: (r) => onSent(r.email) });
  return (
    <Dialog title={`Convidar ${user.consultantName}`} onClose={onClose}>
      <div className="form">
        <ErrorAlert error={m.error} />
        <p>
          Enviar para <strong>{user.email}</strong> um convite com um link para criar a senha (válido por 72 horas, uso único). A senha nunca vai por e-mail.
        </p>
        {user.lastInviteAt && <p className="small muted">Último convite: {dateTime(user.lastInviteAt)}. O link anterior deixa de valer.</p>}
        <LocalLinkWarning appBaseUrl={appBaseUrl} />
        <div className="actions">
          <button className="btn btn-primary" disabled={m.isPending} onClick={() => m.mutate()}>
            {m.isPending ? 'Enviando…' : 'Enviar convite'}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// O e-mail é o login: a troca vale a partir do próximo acesso e fica registrada na auditoria.
function EditEmailDialog({ user, isSelf, onClose, onSaved }: { user: UserRow; isSelf: boolean; onClose: () => void; onSaved: () => void }) {
  const [email, setEmail] = useState(user.email);
  const m = useMutation({ mutationFn: () => api.patch(`/users/${user.id}`, { email }), onSuccess: onSaved });
  const unchanged = email.trim().toLowerCase() === user.email;
  return (
    <Dialog title={`Alterar e-mail de ${user.consultantName}`} onClose={onClose}>
      <form
        className="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorAlert error={m.error} />
        <Field label="E-mail de login" required error={fieldError(m.error, 'email')} hint={`Atual: ${user.email}`}>
          {(p) => <input {...p} type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <p className="small muted">
          {isSelf ? 'No próximo acesso, entre com o novo e-mail.' : `Avise ${user.consultantName}: no próximo acesso, o login passa a ser o novo e-mail.`} A senha não muda.
        </p>
        <div className="actions">
          <button className="btn btn-primary" disabled={m.isPending || !email.trim() || unchanged}>
            Salvar
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </form>
    </Dialog>
  );
}

interface CreatedUser {
  temporaryPassword: string;
  notice: string;
  invitedEmail?: string;
  inviteError?: string;
}

function CreateUserDialog({
  consultants,
  emailConfigured,
  appBaseUrl,
  onClose,
  onCreated,
}: {
  consultants: Consultant[];
  emailConfigured: boolean;
  appBaseUrl: string;
  onClose: () => void;
  onCreated: (r: CreatedUser) => void;
}) {
  const [consultantId, setConsultantId] = useState(consultants[0]?.id ?? '');
  const [email, setEmail] = useState(consultants[0]?.email ?? '');
  const [isAdmin, setIsAdmin] = useState(false);
  const [sendInvite, setSendInvite] = useState(emailConfigured);
  // Com convite, a senha temporária não é exibida: a pessoa cria a própria senha pelo link. Se o envio falhar, a senha aparece como alternativa.
  const m = useMutation({
    mutationFn: async (): Promise<CreatedUser> => {
      const created = await api.post<{ id: string; temporaryPassword: string; notice: string }>('/users', { consultantId, email, isAdmin });
      if (!sendInvite) return created;
      try {
        const r = await api.post<{ email: string }>(`/users/${created.id}/invite`);
        return { ...created, invitedEmail: r.email };
      } catch (err) {
        return { ...created, inviteError: err instanceof ApiError ? err.message : 'falha no envio' };
      }
    },
    onSuccess: onCreated,
  });
  return (
    <Dialog title="Criar conta de acesso" onClose={onClose}>
      <form
        className="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorAlert error={m.error} />
        <Field label="Consultor" required>
          {(p) => (
            <select
              {...p}
              value={consultantId}
              onChange={(e) => {
                setConsultantId(e.target.value);
                setEmail(consultants.find((c) => c.id === e.target.value)?.email ?? '');
              }}
            >
              {consultants.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="E-mail de login" required error={fieldError(m.error, 'email')}>
          {(p) => <input {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <label className="check">
          <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} /> Administrador (gerencia contas e credenciais)
        </label>
        <label className="check">
          <input type="checkbox" checked={sendInvite} disabled={!emailConfigured} onChange={(e) => setSendInvite(e.target.checked)} /> Enviar convite por e-mail
          para o primeiro acesso{!emailConfigured && ' (configure o e-mail em Integrações)'}
        </label>
        {sendInvite && <LocalLinkWarning appBaseUrl={appBaseUrl} />}
        <div className="actions">
          <button className="btn btn-primary" disabled={m.isPending || !consultantId || !email}>
            Criar
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// Logo da TECHNOVE (seção 14): PNG/JPEG, validada no servidor. Documentos já enviados mantêm a logo anterior.
function LogoUploader() {
  const qc = useQueryClient();
  const [stamp, setStamp] = useState(Date.now());
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [alt, setAlt] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const org = useQuery({ queryKey: ['organization'], queryFn: () => api.get<Organization & { logoPath: string | null; logoAlt: string | null }>('/organization') });

  async function upload(f: File | undefined) {
    if (!f) return;
    setError(null);
    const fd = new FormData();
    if (alt.trim()) fd.append('alt', alt.trim());
    fd.append('file', f);
    setBusy(true);
    try {
      const res = await fetch('/api/v1/organization/logo', { method: 'POST', body: fd, credentials: 'same-origin' });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new ApiError(res.status, b?.error?.code ?? 'error', b?.error?.message ?? 'Falha no envio.');
      }
      setStamp(Date.now());
      void qc.invalidateQueries({ queryKey: ['organization'] });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const hasLogo = Boolean(org.data?.logoPath);
  return (
    <fieldset>
      <legend>Logo</legend>
      <ErrorAlert error={error} />
      {hasLogo ? (
        <img src={`/api/v1/organization/logo?v=${stamp}`} alt={org.data?.logoAlt ?? 'Logo'} style={{ maxHeight: 64, maxWidth: 240, display: 'block', marginBottom: 8 }} />
      ) : (
        <p className="small muted">Nenhuma logo: os documentos usam o nome da empresa no cabeçalho.</p>
      )}
      <div className="form-row" style={{ alignItems: 'end' }}>
        <div className="field">
          <label htmlFor="logo-alt">Texto alternativo</label>
          <input id="logo-alt" value={alt} placeholder={org.data?.logoAlt ?? 'Logo TECHNOVE'} onChange={(e) => setAlt(e.target.value)} />
        </div>
        <div className="actions">
          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? 'Enviando…' : hasLogo ? 'Trocar logo' : 'Enviar logo'}
          </button>
          <input ref={fileRef} type="file" hidden aria-label="Arquivo da logo (PNG ou JPEG)" accept="image/png,image/jpeg" onChange={(e) => void upload(e.target.files?.[0])} />
        </div>
      </div>
      <span className="hint">PNG ou JPEG até 1 MB, mínimo 64×32 px. A nova logo vale para documentos novos; os já enviados são preservados.</span>
    </fieldset>
  );
}
